import React, { useState, useRef } from "react";
import "./contentpage.css";
import Question from "../components/Question";
import LandingSection from "./LandingSection";
import FloatingPageNav from "../components/FloatingPageNav";

function BitcoinTechPage72() {
  const contentRef = useRef(null);
  const titleText = "Private-Public Keys and Addresses";
  const links = {
    prev: "/bitcoin-tech-page-71",
    index: "/index-bitcoin-tech",
    next: "/bitcoin-tech-page-73",
  };

  const questions = [
    {
      text: "1. What is the primary purpose of a Bitcoin private key?",
      options: [
        {
          value: "A",
          label: "A. To publicly identify a Bitcoin user on the network",
        },
        {
          value: "B",
          label: "B. To sign transactions and prove ownership of Bitcoin funds",
        },
        {
          value: "C",
          label: "C. To generate transaction fees automatically",
        },
        {
          value: "D",
          label: "D. To store Bitcoin balances on the blockchain",
        },
      ],
      correct: "B",
    },
    {
      text: "2. Why is it practically impossible to guess a Bitcoin private key?",
      options: [
        {
          value: "A",
          label:
            "A. Because private keys are protected by passwords chosen by users",
        },
        {
          value: "B",
          label:
            "B. Because private keys are stored only on offline hardware wallets",
        },
        {
          value: "C",
          label:
            "C. Because the private key space is extremely large, spanning 256-bit numbers",
        },
        {
          value: "D",
          label:
            "D. Because private keys change automatically after every transaction",
        },
      ],
      correct: "C",
    },
    {
      text: "3. How is a Bitcoin public key derived from a private key?",
      options: [
        {
          value: "A",
          label: "A. By hashing the private key using SHA-256 and RIPEMD-160",
        },
        {
          value: "B",
          label:
            "B. By encrypting the private key with a central authority’s key",
        },
        {
          value: "C",
          label:
            "C. By performing elliptic curve multiplication on the secp256k1 curve",
        },
        {
          value: "D",
          label: "D. By encoding the private key using Base58Check",
        },
      ],
      correct: "C",
    },
    {
      text: "4. What is the main reason Bitcoin uses Base58Check encoding for addresses?",
      options: [
        {
          value: "A",
          label: "A. To reduce transaction fees by compressing blockchain data",
        },
        {
          value: "B",
          label: "B. To eliminate the need for cryptographic hashing",
        },
        {
          value: "C",
          label:
            "C. To improve readability and include checksum-based error detection",
        },
        {
          value: "D",
          label: "D. To allow addresses to store private keys securely",
        },
      ],
      correct: "C",
    },
  ];
  // State to track selected answers for all questions
  const [answers, setAnswers] = useState({});

  // Function to update an answer
  const handleAnswerChange = (questionIndex, selectedValue) => {
    setAnswers((prev) => ({ ...prev, [questionIndex]: selectedValue }));
  };

  // Compute score
  const score = questions.reduce((total, q, index) => {
    return total + (answers[index] === q.correct ? 1 : 0);
  }, 0);
  const privkey_range =
    "1 and 2^256 - 1 (or 115792089237316195423570985008687907852837564279074904382605163141518161494336)";
  const hex_privkey =
    "D4F6A8B3C2D4E7F8A1B2C3D4E5F6A7B8C9D0E1F2A3B4C5D6E7F8A9B0C1D2E3";
  const hex_privkey_SHA256 = "SHA-256(SHA-256((80D4F6...))  → 9C8D7E6F...";
  const version = "80";
  const checksum = "9C8D7E6F";
  const WIF_privkey = "5HueCGU8rMjxEXxiPuD5BDuA8A9Nfn8t8aN7xRLcD8A6cV5dTxR";
  const priv_to_pub_conversion = `from coincurve import PublicKey

# Given private key in hex
private_key_hex = "D4F6A8B3C2D4E7F8A1B2C3D4E5F6A7B8C9D0E1F2A3B4C5D6E7F8A9B0C1D2E3F"

# Convert private key to bytes
private_key_bytes = bytes.fromhex(private_key_hex)

# Compute public key using secp256k1 curve
public_key = PublicKey.from_valid_secret(private_key_bytes)

# Get uncompressed and compressed public keys
public_key_uncompressed = public_key.format(compressed=False).hex()
public_key_compressed = public_key.format(compressed=True).hex()

print(f"Uncompressed Public Key: {public_key_uncompressed}")
print(f"Compressed Public Key: {public_key_compressed}")`;

  const dervied_pub_keys = `Uncompressed Public Key: 044f6b9189b5eee1e8577863fd587e12f9a67b4ef0e2118e2a33e55bf149088d699b7bde3045dc2959c2115630856f6a359f8457a21a474902350f333c6f93f073
Compressed Public Key: 034f6b9189b5eee1e8577863fd587e12f9a67b4ef0e2118e2a33e55bf149088d69`;

  const pub_key_to_address = `import hashlib

#This is the public key
publicKey = '044f6b9189b5eee1e8577863fd587e12f9a67b4ef0e2118e2a33e55bf149088d699b7bde3045dc2959c2115630856f6a359f8457a21a474902350f333c6f93f073'

#The public key is currently in hex format, the hash input needs it to be in a bytearray.
publicKeyAsbyteArr = bytearray.fromhex(publicKey)

#Below we are perfroming the first sha256 hash. The digest() converts the output to a bytearray format.
#If this was changed to .hexdigest() the output would be readable and come out in hex.
firstHash = hashlib.sha256(publicKeyAsbyteArr).digest()

#This line of code will apply ripemd160 hash to the first hash and output the result as a hexdigest.
#The reason we need the output in the form of a hexdigest instead of a byte array is because it is easier to append the prefix.
rm160 = hashlib.new('ripemd160', firstHash).hexdigest()

#The prefix tells us what the address represents, 00 means it is a main-net public key address.
r1 = '00' + rm160

#convert r1 to a byte array so that we can use it to perform a checksum.
r1Asbyte = bytearray.fromhex(r1)

#Here we perform a double hash and output the result as hex. This is the checksum.
dh = hashlib.sha256(hashlib.sha256(r1Asbyte).digest()).hexdigest()

#We then take the first four bytes of the cheksum and append it to 'r1'
address = (r1 + dh[0:8])

#This is the address
print("Generated Bitcoin Address:", address)
`;
  const base58_address = `0091815b4b8432cd038e53e6961185dd6df82be731de9379c2`;
  return (
    <div className="container">
      <LandingSection
        titleText={titleText}
        scrollToRef={contentRef}
        progressValue={72}
        timerMinutes={60}
        currentLevel={6}
        unlockedBadges={11}
      />
      <p className="content" ref={contentRef}>
        Welcome to <b>Day 72</b>! of your Bitcoin journey! Yesterday, we
        explored what Bitcoin is, and today, we are going to dive into one of
        the most important topics in Bitcoin—Private and Public Keys. If you
        have ever wondered how Bitcoin ownership works or how transactions are
        secured, this lesson is for you!
        <br />
        <br />
        In computer science, the concept of bits is fundamental to understanding
        how data is stored, processed, and secured. A bit is the smallest unit
        of information in computing, representing either a 0 or a 1. Groups of
        bits form larger structures, such as bytes, words (16 or 32 bits), and
        so forth. Bitcoin, like most cryptographic systems, relies on a
        fixed-length bit sequence to establish security. Specifically, a Bitcoin
        private key consists of 256 bits, which serve as the foundation for
        Bitcoin’s asymmetric cryptography.
      </p>
      <h2>Private Keys</h2>
      <p className="content">
        A private key in Bitcoin is a randomly generated 256-bit number that
        allows users to control their Bitcoin funds. It is required to sign
        transactions, proving ownership of the associated Bitcoin without
        revealing the key itself. Losing a private key means losing access to
        the funds forever, as there is no way to recover it. If a private key is
        exposed, anyone can use it to move the funds, leading to theft.
        <br />
        <br />
        <b>Private Key Range</b> <br />
        <br />A Bitcoin private key is simply a number within a specific range.
        The valid range for a private key is any number between:
      </p>
      <div className="code-block">
        <pre>{privkey_range}</pre>
      </div>
      <p className="content">
        This range ensures that private keys are practically impossible to guess
        due to their immense size.
        <br />
        <br />
        <b>How is a Private Key Generated?</b>
        <br />
        <br />
        To understand how a private key is generated, one must first recognize
        the importance of entropy. Entropy in computing refers to randomness
        collected by an operating system or application to ensure
        unpredictability in cryptographic processes. Secure private key
        generation depends on high-quality entropy to prevent predictability,
        which could otherwise compromise security.
        <br />
        Cryptographically Secure Pseudorandom Number Generators (CSPRNGs) are
        used to generate Bitcoin private keys. CSPRNGs ensure that the
        randomness is sufficiently unpredictable and resistant to manipulation.
        These generators take high-entropy sources such as hardware noise or
        environmental randomness and produce a secure random 256-bit number.
        <br />
        <br />
        <b>Other Sources of Random Entropy:</b>
        <br />
        <br />
        A cryptographic system may generate entropy using various methods, such
        as:
        <br />
        Mouse movements and keystroke timings
        <br />
        Hardware-based randomness (e.g., quantum fluctuations, thermal noise)
        <br />
        System processes and unpredictable environment factors
        <br />
        <br />A randomly generated 256-bit number (expressed in hexadecimal,
        where each character represents 4 bits) could look like this:
      </p>
      <div className="code-block">
        <pre>{hex_privkey}</pre>
      </div>
      <p className="content">
        This represents a full-length private key before any encoding or
        standardization. <br />
        <br />
        <b>Standardizing the Private Key</b>
        <br />
        <br />
        Bitcoin employs encoding methods to make private keys more readable and
        error-resistant when entered manually. <br />
        The process follows these steps:
        <br />
        - Adding a Version Byte: A prefix is added to indicate the type of key.
        For standard Bitcoin private keys, 0x80 is used. This ensures
        compatibility across wallets.
        <br />- Computing the Checksum: The SHA-256 hash of the private key is
        taken, and then hashed again with SHA-256. The first 4 bytes of the
        resulting hash serve as the checksum.
        <br />- Appending the Checksum: These 4 bytes are appended to the
        private key.
        <br />- Encoding in Base58: The entire structure (version byte + private
        key + checksum) is encoded in Base58, eliminating ambiguous characters
        and improving human readability.
        <br />
        <br />
        <b>Example of a Full Private Key Conversion</b>
        <br />
        <br /> 256-bit hex digest of the random data :
      </p>
      <div className="code-block">
        <pre>{hex_privkey}</pre>
      </div>
      <p className="content">With 0x80 prefix (hex):</p>
      <div className="code-block">
        <pre>
          <span style={{ color: "yellow" }}>{version}</span>
          {hex_privkey}
        </pre>
      </div>
      <p className="content">Double SHA-256 hash (checksum derivation):</p>
      <div className="code-block">
        <pre>{hex_privkey_SHA256}</pre>
      </div>
      <p className="content">
        Appending first 4 bytes of checksum: 9C8D7E6F
        <br />
      </p>
      <div className="code-block">
        <pre>
          {version}
          {hex_privkey}
          <span style={{ color: "yellow" }}>{checksum}</span>
        </pre>
      </div>
      <p className="content">Base58 Encoding:</p>
      <div className="code-block">
        <pre>{WIF_privkey}</pre>
      </div>
      <p className="content">
        This is the final Wallet Import Format (WIF) private key, used in
        Bitcoin wallets for easy import and management.
        <br />
        <br />
        <b>Importance of Keeping Private Keys Secure</b>
        <br />
        <br />
        The security of a Bitcoin private key is paramount, as it grants control
        over the corresponding funds. If a private key is exposed, anyone with
        access to it can sign transactions, effectively stealing the associated
        Bitcoin. This makes the secrecy and secure storage of private keys one
        of the most critical aspects of Bitcoin security.
        <br />
        <br />
        <b>Consequences of Losing a Private Key:</b>
        <br />
        <br />
        Loss of access to funds: There is no way to recover lost funds if a
        private key is lost. Theft of Bitcoin: If someone gains access to the
        key, they can move the funds without the owner’s consent. No central
        authority for recovery: Unlike banks, Bitcoin has no institution that
        can help restore access if a private key is lost.
        <br />
        <br />
        Various methods exist to protect private keys, including hardware
        wallets, air-gapped cold storage, multisignature schemes, and seed
        phrase backups.
        <br />
        A common approach to securely managing private keys is the use of
        hierarchical deterministic (HD) wallets, which derive multiple private
        keys from a single master seed. This seed is typically represented as a
        mnemonic phrase using the BIP-39 standard, allowing users to back up
        their entire wallet with a series of 12 or 24 words. The mnemonic phrase
        is converted into a binary seed through a key-stretching function, which
        is then used to derive a deterministic sequence of private keys. This
        approach simplifies backup and recovery while maintaining security.
        <br />
        Another widely adopted method for securing private keys is the use of
        hardware wallets, which store private keys in a tamper-resistant
        environment and sign transactions internally without exposing the key to
        potentially compromised computers. This mitigates the risk of key theft
        through malware or phishing attacks.
        <br />
        Beyond individual key security, the importance of private key secrecy
        extends to broader Bitcoin network security. If private keys were
        predictable or poorly generated, an attacker could systematically scan
        the key space and extract funds. This is why the quality of entropy used
        in key generation is crucial, and why deterministic but unpredictable
        derivation methods are preferred over user-generated randomness.
        <br />
      </p>
      <h2>Public Keys</h2>
      <p className="content">
        For centuries, locks and keys have been used to secure valuables, with
        the assumption that the same key that locks something must also be used
        to unlock it. However, modern cryptography challenges this idea. Instead
        of a single key for both actions, public-key cryptography introduces a
        system where locking and unlocking require different keys.
        <br />
        In Bitcoin, a public key acts as a digital lock. When someone sends
        bitcoin to you, they essentially secure it under your public key,
        ensuring that only you—holding the corresponding private key—can access
        and spend it. Think of it as a special kind of lockbox: anyone can drop
        something inside and secure it, but only the rightful owner possesses
        the unique key to retrieve its contents.
        <br />
        This system allows bitcoin to be transferred securely without the sender
        needing access to the recipient’s private key. While Bitcoin users
        typically interact with addresses rather than raw public keys, these
        addresses are derived from the public key itself. They serve as the
        identifiers where bitcoin can be sent, but only the private key holder
        can authorize transactions.
        <br />
        By separating the ability to lock and unlock, public-key cryptography
        ensures secure, trustless transactions. Bitcoin leverages this
        technology to allow individuals to receive and spend digital assets
        without exposing sensitive information, reinforcing the principles of
        security and decentralization.
        <br />
        <br />
        <b>
          Understanding How a Bitcoin Public Key is Derived from a Private Key{" "}
        </b>
        <br />
        In Bitcoin, a public key is derived from a private key using elliptic
        curve cryptography (ECC). Specifically, Bitcoin uses an elliptic curve
        called **secp256k1**, which is defined by a mathematical equation. This
        process ensures that while a public key can be easily generated from a
        private key, reversing the operation to obtain the private key from the
        public key is infeasible due to the complexity of the underlying
        mathematics.
        <br />
        <br />
        <b>The Elliptic Curve and Public Key Generation</b>
        <br />
        An elliptic curve is a type of mathematical function that forms a
        symmetric, curved shape when plotted on a graph. <br />
        The secp256k1 curve used in Bitcoin is defined by the equation: <br />
        <br />
        [ y^2 = x^3 + 7 ]<br />
        <br />
        This equation describes all the valid points (x, y) that lie on the
        curve. A Bitcoin public key is simply one of these points.
        <br />
        To generate a public key from a private key, we perform elliptic curve
        multiplication:
        <br />
        <br />
        [ K = k * G ]<br />
        <br />
        <b>k</b> is the private key (a randomly chosen 256-bit number).
        <br />
        <b>G</b> is a special, predefined point on the curve called the
        generator point.
        <br />
        <b>K</b> is the resulting public key, which is another point (x, y) on
        the curve.
        <br />
        Because elliptic curve multiplication is a one-way function, knowing K
        does not reveal k.
        <br /> The only way to find k from K would be to try every possible
        value of k, which is computationally infeasible.
        <br />
        <br />
        <b>What is the Generator Point (G)?</b>
        <br />
        The generator point <b>G</b> is a fixed starting point on the secp256k1
        curve. It is chosen in a way that ensures every possible private key
        results in a unique public key. <br />
        When we multiply the private key by G, we are essentially "hopping"
        along the curve in a deterministic way. The more times we move, the
        further along the curve we land. However, since the total number of
        valid points on the curve is incredibly large, there is no efficient way
        to reverse this process and determine the number of "hops" (the private
        key) from the final landing point (the public key).
        <br />
        <br />
        <b>Compressed vs. Uncompressed Public Keys</b>
        <br />
        Since a public key is a point (x, y) on the elliptic curve, it consists
        of two 256-bit numbers. <br />
        This means an uncompressed public key is:
        <br />
        <br />
        0x04∥x∥y - The prefix <b>0x04</b> indicates it's uncompressed.
        <br />
        <br />- The next 32 bytes represent <b>x</b>.<br />- The final 32 bytes
        represent <b>y</b>.<br />
        <br />
        Because y can always be computed from x using the curve equation, we
        don't need to store both values. Instead, a compressed public key only
        stores x and an additional bit to indicate whether y is even or odd:
        <br />
        <br />- <b>0x02</b> means y is even.
        <br />- <b>0x03</b> means y is odd.
        <br />
        - The next 32 bytes store x.
        <br />
        <br />
        Compressed public keys are smaller (33 bytes instead of 65 bytes), which
        helps reduce storage and bandwidth requirements in Bitcoin transactions.
        <br />
        <br />
        The public key is a point on the secp256k1 curve, generated by
        multiplying the private key with a special generator point. This process
        is easy to perform but impossible to reverse. The public key can be
        stored in an uncompressed (65-byte) or compressed (33-byte) format, with
        the latter being more efficient. Understanding these fundamentals is
        crucial for grasping Bitcoin's security and cryptographic principles.
      </p>
      <br />
      <br />
      This Python script uses the coincurve library to compute a public key from
      a given private key. It outputs both the uncompressed (65-byte) and
      compressed (33-byte) formats. Users can run this script locally to
      understand how private-to-public key conversion works in Bitcoin. Ensure
      you never share your private key, as it grants access to your funds.
      <br />
      <br />
      <div className="code-block">
        <pre>{priv_to_pub_conversion}</pre>
        <button
          className="copy-button"
          onClick={() => {
            navigator.clipboard.writeText(priv_to_pub_conversion);
            alert("Copied to clipboard!");
          }}
        >
          Copy to Clipboard
        </button>
      </div>
      <p className="content">
        The output below shows the public key derived from the given private key
        using the secp256k1 elliptic curve. <br />
        The uncompressed public key (130 hex characters or 65 bytes) starts with
        `04`, followed by the full (x, y) coordinates of the elliptic curve
        point. <br />
        The compressed public key (66 hex characters or 33 bytes) starts with
        `03` (or `02` depending on `y` parity), followed by only the `x`
        coordinate. <br />
        The y-coordinate can be inferred from the curve equation. <br />
        Compressed keys are preferred in Bitcoin because they reduce storage and
        transmission size while maintaining the same security properties.
      </p>
      <div className="code-block">
        <pre>{dervied_pub_keys}</pre>
      </div>
      <h2>Addresses</h2>
      <p className="content">
        A Bitcoin address is a unique alphanumeric string that allows users to
        receive funds. Derived from a public key, early Bitcoin addresses began
        with "1." Similar to how a payee is listed on a check, a Bitcoin address
        serves as the recipient in a transaction. Unlike traditional banking,
        where a name or institution can receive funds, Bitcoin addresses provide
        a flexible abstraction that can represent either a key owner or a
        payment script.
        <br />
        Bitcoin addresses are generated using cryptographic hashing. <br />
        The public key undergoes two hashing steps: <br />
        SHA256, followed by RIPEMD160, resulting in a 20-byte output. This
        process ensures security by making it computationally infeasible to
        derive the original public key from the address.
        <br />
        To enhance readability and minimize errors, Bitcoin addresses use
        encodings like <b>Base58Check </b> encoding, which employs a
        58-character set while omitting characters prone to misreading (such as
        0, O, l, and I). Base58Check encoding also includes a <b>checksum</b>,
        derived by applying SHA256 twice to the data and appending the first
        four bytes of the result. This built-in error detection prevents invalid
        addresses from being mistakenly used.
        <br />
        Encoding in Base58 is preferred over hexadecimal or Base64 because it
        strikes a balance between compactness, readability, and accuracy. <br />
        The encoding process starts with adding a <b>version byte</b>, such as
        `0x00` for addresses or `0x80` for private keys. <br />
        After hashing, the checksum is appended to ensure that any alterations
        during transmission are detectable.
        <br />
        By implementing Base58Check, Bitcoin wallets can instantly recognize
        mistyped addresses, preventing accidental loss of funds. This encoding
        method is widely used beyond addresses, also securing private keys,
        encrypted keys, and script hashes within the Bitcoin network.
      </p>
      <div className="code-block">
        <pre>{pub_key_to_address}</pre>
        <button
          className="copy-button"
          onClick={() => {
            navigator.clipboard.writeText(pub_key_to_address);
            alert("Copied to clipboard!");
          }}
        >
          Copy to Clipboard
        </button>
      </div>
      <p className="content">
        The script will output the below P2PKH Base58 encoded address
      </p>
      <div className="code-block">
        <pre>{base58_address}</pre>
      </div>
      <h2>Some Other Types of Bitcoin Addresses</h2>
      <p className="content">
        Here are the common types of Bitcoin addresses:
        <br />
        <br />
        <b>P2PKH</b> (Pay-to-PubKey Hash) — Legacy Address
        <br />
        Example: 1J7mdg5rbQyUHENYdx39WVWK7fsLpEoXZy
        <br />
        Starts with "1", derived from the public key using RIPEMD-160 and
        SHA-256, and encoded in Base58Check.
        <br />
        <br />
        <b>P2SH</b> (Pay-to-Script Hash) — Script-Based Address
        <br />
        Example: 3J98t1WpEZ73CNmQviecrnyiWrnqRhWNLy
        <br />
        Starts with "3", used for smart contract-like scripts, including
        multi-signature wallets.
        <br />
        <br />
        <b>Bech32</b> (P2WPKH - Pay-to-Witness-Public-Key-Hash)
        <br />
        Example: bc1qw4x6h5d3afgj3xrwyhv95plkhqnnrx3p9mk3nu
        <br />
        Starts with "bc1q", used in SegWit transactions, encoded in Bech32, and
        provides lower fees.
        <br />
        <br />
        <b>Bech32m</b> (P2TR - Pay-to-Taproot)
        <br />
        Example: bc1p5cyxnuxmeuwuvkwfem96lwd6p6ecmckr0y35c6
        <br />
        Starts with "bc1p", used for Taproot addresses, improving privacy and
        efficiency.
        <br />
        <br />
        Today, we learned how Bitcoin uses cryptographic keys to secure
        transactions. A private key, which must be kept secret, is randomly
        generated and serves as the foundation for a public key, derived using
        Elliptic Curve Cryptography. <br />
        The public key, in turn, is hashed with SHA-256 and RIPEMD-160 to create
        a Bitcoin address, which is a shorter and more user-friendly
        representation. This address is further encoded using Base58Check to
        improve readability and error detection. Bitcoin addresses act as
        destinations for receiving funds, with different formats like P2PKH,
        P2SH, and Bech32 supporting various transaction types. Since ownership
        of Bitcoin is tied to private keys, safeguarding them is crucial, as
        anyone with access can control the funds.
      </p>
      <h2>Test your understanding</h2>
      {questions.map((q, index) => (
        <Question
          key={index}
          questionText={q.text}
          options={q.options}
          correctAnswer={q.correct}
          onAnswer={(value) => handleAnswerChange(index, value)}
        />
      ))}
      <div style={{ marginTop: "20px", fontWeight: "bold", fontSize: "1.2em" }}>
        Score: {score} / {questions.length}
      </div>
      <FloatingPageNav
        prevLink={links.prev}
        indexLink={links.index}
        nextLink={links.next}
      />
    </div>
  );
}

export default BitcoinTechPage72;
